import { createContext, PropsWithChildren, useContext, useRef, useState } from 'react'
import { ourUuid } from 'utils'
import { useMutation } from 'react-query'
import { GeneratePrompt } from 'types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { SubjectLineOption, SubjectLineOptionsContext as ISubjectLineOptionsContext, SubjectLineValues } from './Types'
import { generateSubjectLines } from 'components/Wizards/generateSubjectLines'

const SubjectLineOptionsContext = createContext<ISubjectLineOptionsContext>({
  abortGeneration: () => {},
  subjectLineOptions: [],
  generateSubjectLineOptions: () => Promise.reject('Not implemented'),
  updateSubjectLineOptions: () => {},
  updateSubjectLineOption: () => {},
  addSubjectLineOption: () => {},
  values: {
    topic: '',
    audience: '',
    call_to_action: '',
    avoid: '',
    filename: '',
    title: '',
  },
  isLoading: false,
})

export const SubjectLineOptionsProvider = ({ children }: PropsWithChildren) => {
  const workspaceId = useCurrentWorkspaceId()
  const [subjectLineOptions, setSubjectLinesOptions] = useState<Array<SubjectLineOption>>([])
  const [values, setValues] = useState<SubjectLineValues>()
  const { mutateAsync, isLoading } = useMutation(generateSubjectLines)

  const abortController = useRef<AbortController | null>(null)
  if (abortController.current === null) abortController.current = new AbortController()

  const abortGeneration = () => {
    abortController.current?.abort()
    abortController.current = new AbortController()
  }

  const addSubjectLineOption = () => {
    setSubjectLinesOptions((oldSubjectLinesOptions) => {
      return [...oldSubjectLinesOptions, [ourUuid(), '']]
    })
  }

  const updateSubjectLineOption = (newSubjectLineOption: SubjectLineOption) => {
    setSubjectLinesOptions((oldSubjectLinesOptions) => {
      return oldSubjectLinesOptions.map((t) => {
        if (t[0] === newSubjectLineOption[0]) return newSubjectLineOption
        return t
      })
    })
  }

  const generateSubjectLineOptions = async (payload?: SubjectLineValues, onComplete?: () => void) => {
    if (payload) setValues(payload)
    const generateValues = payload || values
    if (!generateValues) return Promise.reject('No values passed')

    const prompt: GeneratePrompt = { ...generateValues, generation_type: ChannelType.Newsletter }
    const options = await mutateAsync({
      payload: {
        prompt,
        workspaceId: workspaceId,
      },
      signal: abortController.current?.signal,
    })
    onComplete?.()
    const subjectLineOptions: Array<SubjectLineOption> = options.map((t) => [ourUuid(), t])

    setSubjectLinesOptions((oldSubjectLinesOptions) => {
      return [...oldSubjectLinesOptions, ...subjectLineOptions]
    })
    return subjectLineOptions
  }

  return (
    <SubjectLineOptionsContext.Provider
      value={{
        abortGeneration,
        subjectLineOptions,
        generateSubjectLineOptions,
        updateSubjectLineOptions: setSubjectLinesOptions,
        updateSubjectLineOption,
        addSubjectLineOption,
        isLoading,
        values,
      }}
    >
      {children}
    </SubjectLineOptionsContext.Provider>
  )
}

export const useSubjectLineOptions = () => useContext(SubjectLineOptionsContext)
